import { cva } from 'class-variance-authority';

export const RADIO_BUTTON_STATE = {
  DEFAULT: 'default',
  DESTRUCTIVE: 'destructive',
  DISABLED: 'disabled'
} as const;

// Base radio button control (the circular button itself)
export const baseRadioButtonStyle = [
  'peer',
  'inline-flex',
  'shrink-0',
  'items-center',
  'justify-center',
  'rounded-full',
  'border',
  'cursor-pointer',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-cic-blue-500',
  'focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed',
  'disabled:opacity-100', 
].join(' ');

// Radio button variants based on Figma design
export const radioButtonRootVariants = cva(baseRadioButtonStyle, {
  variants: {
    state: {
      // Default state (blue)
      [RADIO_BUTTON_STATE.DEFAULT]: [
        'data-[state=unchecked]:bg-primary',
        'data-[state=unchecked]:border-primary',
        'data-[state=checked]:bg-pressed-alt',
        'data-[state=checked]:border-strong'
      ].join(' '),
      // Destructive state (red) - all states
      [RADIO_BUTTON_STATE.DESTRUCTIVE]: [
        // Unchecked: white bg, error-500 border
        'data-[state=unchecked]:bg-primary',
        'data-[state=unchecked]:border-negative',
        // Checked: error-500 bg (#F04438), error-500 border
        'data-[state=checked]:bg-destructive',
        'data-[state=checked]:border-negative'
      ].join(' '),
      // Disabled state (gray)
      [RADIO_BUTTON_STATE.DISABLED]: [
        // All disabled: gray-75 bg, gray-200 border, disabled text
        'bg-disabled',
        'border-disabled',
        'text-disabled'
      ].join(' ')
    }
  },
  defaultVariants: {
    state: RADIO_BUTTON_STATE.DEFAULT
  }
});

// Wrapper style for radio button with label
export const radioButtonWrapperStyle = [
  'flex',
  'items-start',
  'gap-2xs',
  'group'
].join(' ');

// Label container style
export const radioButtonLabelContainerStyle = [
  'flex',
  'flex-col',
  'gap-5xs'
].join(' ');

// Label with icon wrapper style
export const radioButtonLabelWithIconStyle = [
  'flex',
  'items-center',
  'gap-3xs'
].join(' ');

// Primary text (label) style - default
export const radioButtonLabelStyle = [
  'font-body',
  'font-medium',
  'text-sm',
  'leading-2xs',
  'text-primary',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Primary text (label) style - destructive
export const radioButtonLabelDestructiveStyle = [
  'font-body',
  'font-medium',
  'text-sm',
  'leading-2xs',
  'text-negative',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Supporting text style - default
export const radioButtonSupportingTextStyle = [
  'font-caption',
  'text-xs',
  'font-normal',
  'leading-xs',
  'text-secondary',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Supporting text style - destructive
export const radioButtonSupportingTextDestructiveStyle = [
  'font-caption',
  'text-xs',
  'font-normal',
  'leading-xs',
  'text-negative',
  'cursor-pointer',
  'group-has-[:disabled]:cursor-not-allowed',
  'group-has-[:disabled]:text-disabled'
].join(' ');

// Inner dot style for selected state
export const radioButtonDotStyle = [
  'block',
  'w-3xs',
  'h-3xs',
  'rounded-full',
  'bg-primary',
  'group-has-[:disabled]:bg-gray-400',
  'shrink-0'
].join(' ');
